import { PersonButton } from '@/features/people/PersonDrawer';
import { BackToOrigin } from '@/lib/origin';
import { useMobileLayout } from '@/mobile/layout';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { usePinToLatest } from './pinToLatest';
import { onListKeyDown } from './reply';
import { HelpButton } from '@/components/help/HelpButton';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { ArrowDown, CalendarDays, Columns2, FolderKanban, Search } from 'lucide-react';
import { Archive, ArchiveRestore, Pencil, Bell, BellOff, ListChecks, Settings2, ClipboardList, Hash, Rows3, Table2, Lock, LogOut, MoreVertical, Phone, Pin, Users, UsersRound, Video, AtSign, Sparkles } from 'lucide-react';
import { CHANNEL_CANNOT_POST, COPY_MEMBERSHIP_LABEL, canCopyChannelMembership, myChannelActions, type ChatSearchHit, type Conversation, type Message } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { dayDivider, fullDateTime } from '@/lib/format';
import { useModuleOn } from '@/lib/modules';
import { conversationName, useConversations, useChatSections, useProjects, useTaskLinks, useWorkspaceId } from '@/lib/hooks';
import { useCanAnywhere, usePermissionsAt } from '@/lib/permissions';
import { sectionPath } from '@/lib/sectionTree';
import { messageStore, useConversationState } from '@/lib/messageStore';
import { qk, queryClient } from '@/lib/queryClient';
import { activeConversation } from '@/lib/RealtimeBridge';
import { useUi } from '@/lib/store';
import { useNarrow } from '@/lib/useMedia';
import { Avatar, Button, Center, DatePickerButton, ErrorBox, Menu, MenuItem, Spinner, ask } from '@/components/ui';
import { Composer } from './Composer';
import { MessageItem, PendingItem } from './MessageItem';
import { CHAT_COLUMN, bubbleNames, runPositions, type BubbleRun } from './bubbles';
import { pairCalls } from './callLines';
import { ChatSearchBar } from './ChatSearchBar';
import { flashMessage, jumpToDateNote, londonDate, paintHighlights, searchTerms } from './chatSearch';
import { ThreadPanel } from './ThreadPanel';
import { ThreadPrefsProvider, ThreadsToggle } from './InlineThread';
import { MembersModal, NewChannelModal, PinsModal } from './ConversationModals';
import { BuildingTag } from '@/features/buildings/common';
import { ChannelSettingsModal } from './ChannelSettingsModal';
import { MembersButton, MembersPanel } from './MembersPanel';
import { useStartCall } from '@/features/calls/useStartCall';
import { usePrepareCall } from '@/features/calls/prepare';
import { FormComposer } from '@/features/forms/FormPost';
import { ChannelFormModal, SubmissionsTable } from '@/features/forms/Submissions';
import { PollCreateModal, type PollDraft } from '@/features/polls/PollCreateModal';
import { AckRequestModal, type AckDraft } from '@/features/acks/Acks';
import { CatchUpModal, useCanCatchUp } from '@/features/ai/CatchUp';
import { RoomIcon } from '@/features/rooms/RoomIcon';
import { RoomMembersStrip } from '@/features/rooms/RoomMembersStrip';
import { PhoneBack } from './PhoneBack';
import { RoomSettingsModal, closeRoom, reopenRoom } from '@/features/rooms/roomActions';

import { jumpToMessage } from '@/lib/jump';
import { TypingLine } from './TypingLine';


/** `conversationId` + `embedded`: the project's split view (t123) shows its channel next to the tasks. */
export function ConversationPage({ conversationId, embedded }: { conversationId?: string; embedded?: boolean } = {}) {
  const routeParams = useParams();
  const id = conversationId ?? routeParams.id ?? '';
  const [params, setParams] = useSearchParams();
  const ws = useWorkspaceId();
  const { userId } = useAuth();
  const convs = useConversations();
  const fromList = convs.data?.conversations.find(c => c.id === id);
  const detail = useQuery({ queryKey: qk.conversation(id), queryFn: () => api.conversation(id) });
  useTaskLinks(id, true); // single polling observer for the task chips
  const sections = useChatSections();
  const conv: Conversation | undefined = detail.data ?? fromList;
  const isChannel = conv?.kind === 'channel';
  const formInfo = useQuery({ queryKey: ['channel-form', id, conv?.form_template_id ?? null], queryFn: () => api.channelForm(id), enabled: Boolean(isChannel) });
  const formTemplate = conv?.form_template_id ? formInfo.data?.template ?? null : null;
  useEffect(() => { if (!formTemplate) setView('posts'); }, [formTemplate]);
  const state = useConversationState(id);
  const threadRootId = useUi(s => s.threadRootId);
  const openThread = useUi(s => s.openThread);
  const toast = useUi(s => s.toast);
  const scrollRef = useRef<HTMLDivElement>(null);
  const atBottom = useRef(true);
  const prevHeight = useRef<number | null>(null);
  const [modal, setModal] = useState<null | 'pins' | 'members' | 'form' | 'settings' | 'room' | 'copy'>(null);
  const openNewRoom = useUi(s => s.setNewRoomOpen);
  const [view, setView] = useState<'posts' | 'table'>('posts');
  const [pollDraft, setPollDraft] = useState<PollDraft | null>(null);
  const [ackDraft, setAckDraft] = useState<AckDraft | null>(null);
  // Effective permissions in this conversation (server-resolved, includes building/project placement).
  const here = usePermissionsAt(`conversation:${id}`).data;
  const [menu, setMenu] = useState(false);
  const narrow = useNarrow();
  // Phone (mobile Phase 1): full screen, no inline threads; a thread is its own screen pushed as ?thread= so Back closes it.
  const phone = useMobileLayout() && !embedded;
  const urlThread = params.get('thread');
  const pushedThread = useRef(false);
  useEffect(() => {
    if (!phone || !threadRootId || urlThread === threadRootId) return;
    const next = new URLSearchParams(params); next.set('thread', threadRootId);
    pushedThread.current = true;
    setParams(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- follow the open thread only
  }, [phone, threadRootId]);
  useEffect(() => {
    if (!phone) return;
    if (!urlThread && threadRootId) openThread(null);
    else if (urlThread && urlThread !== threadRootId) openThread(urlThread);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- follow the URL (Back / Forward) only
  }, [phone, urlThread]);
  const closeThread = () => {
    if (phone && pushedThread.current && urlThread) { pushedThread.current = false; window.history.back(); return; }
    openThread(null);
    if (params.get('thread')) { params.delete('thread'); setParams(params, { replace: true }); }
  };
  const [catchUp, setCatchUp] = useState(false);
  // t369u: calls and polls lose their buttons when their modules are off.
  const callsOn = useModuleOn('calls'), pollsOn = useModuleOn('polls');
  const canCatchUp = useCanCatchUp();
  const startCall = useStartCall();
  usePrepareCall(callsOn, id); // t437u: the header's Call buttons connect sooner
  const canCreateRoom = useCanAnywhere('chat.room.create') && callsOn;
  // t557u: start a new channel with this one's people and settings.
  const canCreateChannel = useCanAnywhere('chat.channel.create');
  // "New messages" divider anchored to the read position when the conversation was opened
  const [unreadFrom, setUnreadFrom] = useState<number | null>(null);
  // t291u: in-chat search and Jump to date.
  const [searching, setSearching] = useState<null | 'search' | 'date'>(null);
  const [searchText, setSearchText] = useState('');
  const [currentHit, setCurrentHit] = useState<string | null>(null);
  const [away, setAway] = useState(false);

  useEffect(() => {
    activeConversation.id = id;
    void messageStore.load(id).then(() => messageStore.catchUp(id));
    const t = params.get('thread');
    openThread(t);
    return () => { activeConversation.id = null; openThread(null); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { setUnreadFrom(null); setSearching(null); setSearchText(''); setCurrentHit(null); setAway(false); }, [id]);
  useEffect(() => {
    if (unreadFrom === null && fromList) setUnreadFrom(fromList.unread_count > 0 ? fromList.last_read_sequence : -1);
  }, [fromList, unreadFrom]);

  // Mark read when the newest message is on screen
  const lastSeq = state.messages.at(-1)?.sequence ?? 0;
  const markRead = useCallback(() => {
    if (!ws || !fromList || document.hidden || !atBottom.current) return;
    const latest = Math.max(lastSeq, state.cursor);
    if (latest > fromList.last_read_sequence || fromList.unread_count > 0) {
      queryClient.setQueryData<{ conversations: Conversation[] }>(qk.conversations(ws), old => old && ({
        conversations: old.conversations.map(c => (c.id === id ? { ...c, last_read_sequence: latest, unread_count: 0, mention_count: 0 } : c)),
      }));
      void api.markRead(id, latest).catch(() => undefined);
    }
  }, [ws, fromList, lastSeq, state.cursor, id]);
  useEffect(() => { const t = setTimeout(markRead, 600); return () => clearTimeout(t); }, [markRead]);
  useEffect(() => {
    const h = () => markRead();
    document.addEventListener('visibilitychange', h);
    return () => document.removeEventListener('visibilitychange', h);
  }, [markRead]);

  // Scroll management: stick to bottom, preserve position when older pages load
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (prevHeight.current !== null) {
      el.scrollTop += el.scrollHeight - prevHeight.current;
      prevHeight.current = null;
    } else if (atBottom.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [state.messages, state.pending, state.threads]);

  // t236u: on the phone, tapping into the composer jumps to the latest message and keeps it just above the composer
  // while the keyboard opens, the composer grows and new messages arrive.
  const composerBox = useRef<HTMLDivElement>(null);
  const pinBottom = useCallback(() => { atBottom.current = true; }, []);
  usePinToLatest(scrollRef, composerBox, { enabled: phone, onPin: pinBottom }, [state.status, conv?.is_member, view, id]);

  useEffect(() => {
    const hash = location.hash;
    if (hash.startsWith('#m-') && state.status === 'ready') document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'center' });
  }, [state.status]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    const far = el.scrollHeight - el.scrollTop - el.clientHeight > 400;
    if (far !== away) setAway(far);
    if (atBottom.current) markRead();
    if (el.scrollTop < 200 && state.nextBefore != null && !state.loadingOlder) {
      prevHeight.current = el.scrollHeight;
      void messageStore.loadOlder(id);
    }
  };

  // t291u: bring a message into view - load history back to it (kept contiguous, so realtime and the New marker keep
  // working), scroll it to the middle and flash it. A thread reply opens its thread and flashes the reply there.
  const showMessage = useCallback(async (rootId: string, rootSeq: number, opts: { replyId?: string; block?: ScrollLogicalPosition } = {}) => {
    atBottom.current = false;
    const ok = await messageStore.loadBackTo(id, rootSeq);
    if (!ok) { toast("Couldn't load that message", 'error'); return; }
    requestAnimationFrame(() => requestAnimationFrame(() => {
      document.getElementById(`m-${rootId}`)?.scrollIntoView({ block: opts.block ?? 'center' });
      flashMessage(rootId);
      const el = scrollRef.current;
      if (el) setAway(el.scrollHeight - el.scrollTop - el.clientHeight > 400);
    }));
    // On the phone a thread is its own screen, which would hide the search bar: the root (with its replies line) is
    // shown instead. On a computer the thread opens beside the chat and the reply flashes there.
    if (opts.replyId && !phone) {
      const replyId = opts.replyId;
      openThread(rootId);
      for (let i = 0; i < 30; i++) {
        await new Promise(r => setTimeout(r, 100));
        const el = document.querySelectorAll(`[id="m-${replyId}"]`);
        if (el.length) { el[el.length - 1].scrollIntoView({ block: 'center' }); el.forEach(e => { const b = e.querySelector<HTMLElement>('[data-testid=bubble]') ?? e as HTMLElement; b.classList.add('chat-jump-flash'); setTimeout(() => b.classList.remove('chat-jump-flash'), 1800); }); break; }
      }
    }
  }, [id, toast, openThread, phone]);
  const jumpToHit = useCallback((hit: ChatSearchHit) => {
    setCurrentHit(hit.id);
    void showMessage(hit.parent_id ?? hit.id, hit.root_sequence, { replyId: hit.parent_id ? hit.id : undefined });
  }, [showMessage]);
  const jumpToDate = useCallback(async (iso: string) => {
    try {
      const { message } = await api.messageAtDate(id, iso);
      const note = jumpToDateNote(iso, message?.created_at ?? null);
      if (!message) { toast(note ?? 'No messages on that date', 'info'); return; }
      await showMessage(message.id, message.sequence, { block: 'start' });
      if (note) toast(note, 'info');
    } catch (e) { toast(errorMessage(e), 'error'); }
  }, [id, showMessage, toast]);
  const backToLatest = () => {
    const el = scrollRef.current;
    if (!el) return;
    atBottom.current = true;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    setAway(false);
  };
  const terms = useMemo(() => searchTerms(searchText), [searchText]);

  // t278u: chat reads as bubbles (yours right, others left); item discussions keep the comment layout.
  const bubbleKind = !conv || conv.kind === 'entity' ? null : conv.kind;
  const rows = useMemo(() => {
    const out: ({ type: 'day'; key: string; label: string; iso: string } | { type: 'unread'; key: string } | { type: 'msg'; key: string; m: Message; compact: boolean; run?: BubbleRun; callEnd?: Message })[] = [];
    // t289u: a call's ended note joins its started note on one line (paired by call id), so it isn't listed again.
    const calls = pairCalls(state.messages);
    const list = calls.hidden.size ? state.messages.filter(m => !calls.hidden.has(m.id)) : state.messages;
    // t278u: the New marker starts a fresh run, so the first unread bubble carries its name again.
    const unreadAt = unreadFrom !== null && unreadFrom >= 0 ? list.find(m => m.sequence > unreadFrom && m.sender_id !== userId)?.id : undefined;
    const runs = runPositions(list, unreadAt ? new Set([unreadAt]) : undefined);
    let prev: Message | null = null;
    for (const m of list) {
      const day = dayDivider(m.created_at);
      if (!prev || dayDivider(prev.created_at) !== day) out.push({ type: 'day', key: `d-${m.id}`, label: day, iso: londonDate(m.created_at) });
      if (m.id === unreadAt) out.push({ type: 'unread', key: 'unread' });
      const compact = Boolean(prev && prev.sender_id === m.sender_id && prev.kind === 'user' && m.kind === 'user' && !prev.deleted_at
        && new Date(m.created_at).getTime() - new Date(prev.created_at).getTime() < 5 * 60_000 && dayDivider(prev.created_at) === day);
      out.push({ type: 'msg', key: m.id, m, compact, run: bubbleKind === null ? undefined : { ...(runs.get(m.id) ?? { first: true, last: true }), names: bubbleNames(bubbleKind) }, callEnd: calls.ended.get(m.id) });
      prev = m;
    }
    return out;
  }, [state.messages, unreadFrom, userId, bubbleKind]);

  // t291u: paint the search words on whatever is on screen (main list and the thread panel) as it changes.
  useEffect(() => {
    const root = document.querySelector<HTMLElement>('[data-testid=conversation-page]');
    return paintHighlights(root, searching ? terms : [], currentHit);
  }, [terms, searching, currentHit, rows, state.threads, threadRootId]);

  const [readers, setReaders] = useState(false);
  const [settingsTab, setSettingsTab] = useState<'about' | 'access'>('about');
  const join = async () => {
    try {
      await api.joinChannel(id);
      await queryClient.invalidateQueries({ queryKey: qk.conversations(ws ?? '') });
      await detail.refetch();
      void messageStore.load(id, true);
    } catch (e) { toast(errorMessage(e), 'error'); }
  };

  const setNotify = async (level: 'all' | 'mentions' | 'none') => {
    setMenu(false);
    await api.updateMembership(id, { notify_level: level });
    void queryClient.invalidateQueries({ queryKey: qk.conversations(ws ?? '') });
    toast(level === 'all' ? 'Notifying for all messages' : level === 'mentions' ? 'Notifying for mentions only' : 'Muted', 'success');
  };

  const leave = async () => {
    setMenu(false);
    if (!await ask('Leave this conversation?')) return;
    try { await api.leaveConversation(id); void queryClient.invalidateQueries({ queryKey: qk.conversations(ws ?? '') }); history.back(); } catch (e) { toast(errorMessage(e), 'error'); }
  };

  // Backlog #16/#17: the author, or a member holding chat.ack.manage here, can require acknowledgement.
  const ackCtx = useMemo(() => {
    return { canManage: Boolean(conv?.is_member) && Boolean(here?.has('chat.ack.manage')), label: conv?.kind === 'channel' ? `#${conv.slug}` : 'this conversation', memberCount: conv?.members?.length ?? 0 };
  }, [conv, here]);
  if (detail.isError && !conv) return <Center><ErrorBox error={detail.error} /></Center>;
  if (!conv) return <Center><Spinner /></Center>;

  const name = conversationName(conv, userId);
  const isRoom = conv.kind === 'room';
  const closed = isRoom && Boolean(conv.archived_at);
  const others = conv.kind === 'dm' || conv.kind === 'group_dm' ? (conv.members ?? []).filter(m => m.user_id !== userId) : [];
  const memberIds = conv.members?.map(m => m.user_id);
  const moderator = Boolean(here?.has('chat.poll.manage'));
  // t520u: the channel's permissions (what the server allows this person here).
  const acts = myChannelActions(conv);
  // Polls follow the channel's policy; form channels only take form posts (backlog #13).
  const canPoll = pollsOn && conv.is_member && !conv.archived_at && !conv.form_template_id && acts.post && (conv.poll_policy === 'moderators' ? moderator : Boolean(here?.has('chat.poll.create')));

  // A one-to-one DM: the header avatar and name open the other person's profile drawer.
  const dmPerson = others.length === 1 ? others[0].user_id : null;
  const bubbles = bubbleKind !== null;
  // t287u: on a computer the bubbles, separators and composer share one centred column, so yours and theirs sit close
  // together on wide screens. The header stays full width; the phone is unchanged.
  const column = bubbles && !phone ? CHAT_COLUMN : undefined;
  const onClose = () => { setMenu(false); void closeRoom(conv, ws ?? ''); };
  const onReopen = () => { setMenu(false); void reopenRoom(conv, ws ?? ''); };
  return (
    <div className="flex h-full" data-testid="conversation-page">
      {readers && <MembersPanel conversation={conv} onClose={() => setReaders(false)} onManageAccess={() => { setReaders(false); setSettingsTab('access'); setModal('settings'); }} />}
      <div className={clsx('min-w-0 flex-1 flex-col bg-white dark:bg-slate-950', phone && threadRootId ? 'hidden' : 'flex')}>
        <header className={clsx('flex h-14 shrink-0 items-center gap-2 border-b border-slate-200 px-3 sm:gap-3 sm:px-5 dark:border-slate-800', phone && 'box-content gap-1 pl-1 pr-1 pt-[env(safe-area-inset-top)]')}>
          {phone ? <PhoneBack /> : !embedded && <BackToOrigin compact className="p-1" />}
          {conv.kind === 'channel'
            ? (conv.is_private ? <Lock className="h-4 w-4 shrink-0 text-slate-500" /> : <Hash className="h-4 w-4 shrink-0 text-slate-500" />)
            : isRoom ? <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200" title="Room"><RoomIcon /></span>
            : dmPerson ? <PersonButton userId={dmPerson} name={name} testId="dm-header-avatar" className="shrink-0 rounded-md focus-visible:outline-2"><Avatar size={28} name={name} id={dmPerson} /></PersonButton>
            : <Avatar size={28} name={name} id={others[0]?.user_id ?? conv.id} />}
          <div className="min-w-0 flex-1 sm:flex-initial">
            <div className="flex items-baseline gap-2 truncate">
              {conv.section_id && !narrow && <span className="truncate text-xs text-slate-500">{sectionPath(sections.data?.sections ?? [], conv.section_id)} ›</span>}
              {dmPerson ? <PersonButton userId={dmPerson} name={name} testId="dm-header-name" className="truncate rounded text-left font-semibold hover:underline focus-visible:outline-2">{name}</PersonButton>
                : <span className="truncate font-semibold">{name}</span>}
              {closed && <span className="shrink-0 text-[11px] font-semibold text-slate-700 dark:text-slate-200" data-testid="room-closed-badge">Closed</span>}
            </div>
            {/* t547u: on narrow screens the form name rides on the muted line under the channel name (the header has no room beside it) */}
            {narrow && formTemplate
              ? <div className="truncate text-xs text-slate-500 dark:text-slate-400" data-testid="channel-form-label">{[`${formTemplate.name} form`, conv.topic || conv.description].filter(Boolean).join(' · ')}</div>
              : (conv.topic || conv.description) && <div className="truncate text-xs text-slate-500">{conv.topic || conv.description}</div>}
          </div>
          {conv.kind === 'channel' && conv.location_id && !narrow && <BuildingTag id={conv.location_id} />}
          {conv.project_id && !embedded && <ProjectLinks projectId={conv.project_id} narrow={narrow} />}
          {/* t546u: like the building, beside the name on wide screens only; narrow screens show it on the muted line under the name (t547u) */}
          {formTemplate && !narrow && (
            <span className="flex shrink-0 items-center gap-1 whitespace-nowrap text-xs text-slate-500 dark:text-slate-400" title="Every post in this channel is a filled-in form" data-testid="channel-form-label">
              <ClipboardList className="h-3 w-3" />{formTemplate.name} form
            </span>
          )}
          {/* t547u: on narrow screens one quiet icon button switches Posts / Table; the two-tab switch squeezed the channel name to nothing,
              leaving only a shrunken # (a stray dot) */}
          {formTemplate && narrow && (
            <Button variant="ghost" size="icon" onClick={() => setView(v => v === 'posts' ? 'table' : 'posts')} data-testid="channel-view-toggle"
              title={view === 'posts' ? 'Show as a table' : 'Show posts'} aria-label={view === 'posts' ? 'Show as a table' : 'Show posts'}>
              {view === 'posts' ? <Table2 className="h-4 w-4" /> : <Rows3 className="h-4 w-4" />}
            </Button>
          )}
          {formTemplate && !narrow && (
            <div className="ml-2 flex shrink-0 rounded-lg border border-slate-200 p-0.5 text-xs dark:border-slate-700" role="tablist" aria-label="Channel view">
              <button role="tab" aria-selected={view === 'posts'} onClick={() => setView('posts')} className={clsx('flex items-center gap-1 rounded-md px-2 py-1', view === 'posts' ? 'bg-slate-100 font-semibold dark:bg-slate-800' : 'text-slate-500')}><Rows3 className="h-3.5 w-3.5" />Posts</button>
              <button role="tab" aria-selected={view === 'table'} onClick={() => setView('table')} className={clsx('flex items-center gap-1 rounded-md px-2 py-1', view === 'table' ? 'bg-slate-100 font-semibold dark:bg-slate-800' : 'text-slate-500')}><Table2 className="h-3.5 w-3.5" />Table</button>
            </div>
          )}
          <div className="ml-auto flex shrink-0 items-center gap-1">
            {conv.is_member && !narrow && <ThreadsToggle conversationId={id} />}
            {canCatchUp && <Button variant="ghost" size={narrow ? 'icon' : 'sm'} onClick={() => setCatchUp(true)} title="Summarise what you missed" aria-label="Catch me up"><Sparkles className="h-4 w-4" />{!narrow && 'Catch me up'}</Button>}
            {conv.kind !== 'entity' ? <MembersButton id={id} memberCount={conv.member_count ?? conv.members?.length} onOpen={() => setReaders(true)} />
              : <Button variant="ghost" size="sm" onClick={() => setModal('members')} title="Members" aria-label={`Members (${conv.member_count ?? conv.members?.length ?? 0})`}><Users className="h-4 w-4" />{conv.member_count ?? conv.members?.length ?? ''}</Button>}
            {conv.kind !== 'entity' && <Button variant="ghost" size="icon" onClick={() => setSearching(s => s ? null : 'search')} title="Search this conversation" aria-label="Search this conversation"
              aria-pressed={Boolean(searching)} data-testid="chat-search-toggle"><Search className="h-4 w-4" /></Button>}
            {!narrow && <>
              <Button variant="ghost" size="icon" onClick={() => setModal('pins')} title="Pinned messages" aria-label="Pinned messages"><Pin className="h-4 w-4" /></Button>
              {callsOn && <>
                <Button variant="ghost" size="icon" title="Start audio call" aria-label="Start audio call" disabled={!conv.is_member || closed} onClick={() => void startCall({ conversation_id: conv.id, media: 'audio' })}><Phone className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" title="Start video call" aria-label="Start video call" disabled={!conv.is_member || closed} onClick={() => void startCall({ conversation_id: conv.id, media: 'video' })}><Video className="h-4 w-4" /></Button>
              </>}
            </>}
            {conv.is_member && (
              <Menu open={menu} setOpen={setMenu} trigger={<Button variant="ghost" size="icon" title="Conversation settings" aria-label="Conversation menu"><MoreVertical className="h-4 w-4" /></Button>}>
                {narrow && <>
                  <MenuItem icon={<Pin className="h-4 w-4" />} onClick={() => { setMenu(false); setModal('pins'); }}>Pinned messages</MenuItem>
                  {callsOn && <>
                    <MenuItem icon={<Phone className="h-4 w-4" />} onClick={() => { setMenu(false); void startCall({ conversation_id: conv.id, media: 'audio' }); }}>Audio call</MenuItem>
                    <MenuItem icon={<Video className="h-4 w-4" />} onClick={() => { setMenu(false); void startCall({ conversation_id: conv.id, media: 'video' }); }}>Video call</MenuItem>
                  </>}
                </>}
                {conv.kind !== 'entity' && <MenuItem icon={<CalendarDays className="h-4 w-4" />} onClick={() => { setMenu(false); setSearching('date'); }}>Jump to date…</MenuItem>}
                {canCreateRoom && <MenuItem icon={<RoomIcon />} onClick={() => { setMenu(false); openNewRoom(true); }}>New ad-hoc meeting room with a call…</MenuItem>}
                {isRoom && conv.can_manage && !closed && <MenuItem icon={<Pencil className="h-4 w-4" />} onClick={() => { setMenu(false); setModal('room'); }}>Ad-hoc meeting room settings…</MenuItem>}
                {conv.kind === 'channel' && <MenuItem icon={<Settings2 className="h-4 w-4" />} onClick={() => { setMenu(false); setSettingsTab('about'); setModal('settings'); }}>Channel settings…</MenuItem>}
                {canCopyChannelMembership(conv, canCreateChannel) && <MenuItem icon={<UsersRound className="h-4 w-4 shrink-0" />} onClick={() => { setMenu(false); setModal('copy'); }} data-testid="menu-copy-members">{COPY_MEMBERSHIP_LABEL}</MenuItem>}
                <div role="group" aria-label="Notifications" className="border-y border-slate-100 py-1 dark:border-slate-700">
                  {([['all', 'All messages', Bell], ['mentions', 'Mentions only', AtSign], ['none', 'Mute', BellOff]] as const).map(([lvl, label, Icon]) => (
                    <button key={lvl} type="button" role="menuitemradio" aria-checked={conv.notify_level === lvl} onClick={() => void setNotify(lvl)}
                      className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700">
                      <Icon className="h-4 w-4" /><span className="flex-1">{label}</span>
                      <span className={clsx('h-3.5 w-3.5 rounded-full border-2', conv.notify_level === lvl ? 'border-slate-900 bg-slate-900 ring-2 ring-inset ring-white dark:border-white dark:bg-white dark:ring-slate-800' : 'border-slate-400')} aria-hidden />
                    </button>
                  ))}
                </div>
                {/* t621u: no Set building… here: only channels have a building (Channel settings › About); DMs and group DMs never do. */}
                {canPoll && <MenuItem icon={<ListChecks className="h-4 w-4" />} onClick={() => { setMenu(false); setPollDraft({ kind: 'survey' }); }}>Start a survey…</MenuItem>}
                {isRoom && conv.can_close && (closed
                  ? <MenuItem icon={<ArchiveRestore className="h-4 w-4" />} onClick={onReopen}>Reopen ad-hoc meeting room</MenuItem>
                  : <MenuItem danger icon={<Archive className="h-4 w-4" />} onClick={onClose}>Close ad-hoc meeting room…</MenuItem>)}
                {conv.kind !== 'dm' && <MenuItem danger icon={<LogOut className="h-4 w-4" />} onClick={() => void leave()}>Leave {conv.kind === 'channel' ? 'channel' : isRoom ? 'room' : 'conversation'}</MenuItem>}
              </Menu>
            )}
            {!narrow && <HelpButton />}
          </div>
        </header>
        {/* t271u: the room's people across the top; View all opens the Members panel / sheet. */}
        {isRoom && <RoomMembersStrip members={conv.members ?? []} me={userId} phone={phone} onViewAll={() => setReaders(true)} />}
        {searching && <ChatSearchBar key={searching} conversationId={id} openDate={searching === 'date'} onJump={jumpToHit} onTerms={setSearchText} onPickDate={d => void jumpToDate(d)}
          onClose={() => { setSearching(null); setCurrentHit(null); }} />}
        {catchUp && <CatchUpModal conversationId={conv.id} title={name} onClose={() => setCatchUp(false)} onJump={jumpToMessage} />}

        {view === 'table' && formTemplate ? <SubmissionsTable conversationId={id} template={formTemplate} /> : <>
        <MaybeThreadPrefs on={!phone} conversationId={id} canReply={Boolean(conv.is_member && !conv.archived_at && acts.reply)}>
        <div className="relative flex min-h-0 flex-1 flex-col">
        <div ref={scrollRef} onScroll={onScroll} className="scroll-thin min-h-0 flex-1 scroll-pt-11 overflow-y-auto pb-2 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-300"
          data-message-list="main" tabIndex={0} aria-label="Messages (arrow keys to move, R to reply)" onKeyDown={onListKeyDown}>
          <div className={column} data-testid="chat-column">
          {state.status === 'loading' && <div className="flex justify-center p-8"><Spinner /></div>}
          {state.status === 'error' && <div className="p-6"><ErrorBox error={state.error} /></div>}
          {state.loadingOlder && <div className="flex justify-center p-3"><Spinner /></div>}
          {state.status === 'ready' && state.nextBefore == null && (
            <div className="px-5 pb-4 pt-10">
              <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-xl bg-accent-soft text-accent-fg">{conv.kind === 'channel' ? <Hash className="h-6 w-6" /> : isRoom ? <RoomIcon className="h-6 w-6" /> : <Users className="h-6 w-6" />}</div>
              <div className="text-xl font-bold">{conv.kind === 'channel' ? `Welcome to #${name}` : name}</div>
              <div className="text-sm text-slate-500">{conv.kind === 'channel' ? (conv.description || 'This is the very beginning of the channel.') : isRoom ? 'This is the start of the ad-hoc meeting room. Start a call from the phone or camera button. Messages sent during the call stay here afterwards.' : 'This is the beginning of your direct message history.'}</div>
            </div>
          )}
          {rows.map(r => r.type === 'day' ? (
            <div key={r.key} data-testid="day-label" className="sticky top-0 z-[5] my-1 flex items-center gap-3 bg-white/95 px-5 py-1.5 backdrop-blur-sm dark:bg-slate-950/95">
              <span className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />
              <DatePickerButton value={r.iso} onPick={d => void jumpToDate(d)} title="Jump to date" aria-label={`${r.label}: jump to date`} testId="day-pill"
                className="rounded-full border border-slate-200 bg-white px-3 py-0.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800">{r.label}</DatePickerButton>
              <span className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />
            </div>
          ) : r.type === 'unread' ? (
            <div key={r.key} className="my-1 flex items-center gap-2 px-5"><span className="h-px flex-1 bg-rose-400" /><span className="text-xs font-semibold text-rose-500">New</span></div>
          ) : (
            <MessageItem key={r.key} m={r.m} compact={r.compact} canSetStatus={formInfo.data?.can_set_status} ack={ackCtx} bubble={r.run} callEnd={r.callEnd} />
          ))}
          {state.pending.filter(p => !p.parent_id).map(p => <PendingItem key={p.client_message_id} p={p} conversationId={id} bubble={bubbles} />)}
          </div>
        </div>
        {away && (
          <button type="button" onClick={backToLatest} data-testid="back-to-latest"
            className="absolute bottom-3 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-md hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800">
            <ArrowDown className="h-3.5 w-3.5" />Back to latest
          </button>
        )}
        </div>
        </MaybeThreadPrefs>

        <TypingLine conversationId={id} className={clsx('px-6', column)} />
        <div className={column}>
        {closed ? (
          <div className="m-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900" data-testid="room-closed-banner">
            <span className="flex items-center gap-2 text-sm"><Archive className="h-4 w-4 text-slate-500" />
              <span>This ad-hoc meeting room was closed{conv.closed_by_name ? ` by ${conv.closed_by_name}` : ''}{conv.archived_at ? ` on ${fullDateTime(conv.archived_at)}` : ''}. It's read-only; the messages are kept.</span></span>
            {conv.can_close && <Button variant="secondary" onClick={onReopen}><ArchiveRestore className="h-4 w-4" />Reopen ad-hoc meeting room</Button>}
          </div>
        ) : conv.is_member && !acts.post ? (
          <p className={clsx('px-5 py-4 text-center text-sm text-slate-500', phone && 'safe-b')} data-testid="composer-cannot-post">{CHANNEL_CANNOT_POST}</p>
        ) : conv.is_member ? <div ref={composerBox} className={clsx(phone && 'safe-b')} data-testid="composer-box">{(formTemplate
          ? <FormComposer conversationId={id} template={formTemplate} ownBuildingId={formInfo.data?.own_building_id} createsJourneys={formInfo.data?.creates_journeys} />
          : <Composer conversationId={id} placeholder={`Message ${conv.kind === 'channel' ? `#${name}` : name}`} memberIds={memberIds} autoFocus={!phone}
              canSchedule={conv.kind === 'channel' || conv.kind === 'dm' || conv.kind === 'group_dm'}
              onPoll={canPoll ? d => setPollDraft({ kind: 'poll', question: d.question, options: d.options }) : undefined}
              onPostWithAck={(d, clear) => setAckDraft({ conversationId: id, channelLabel: ackCtx.label, memberCount: ackCtx.memberCount, send: d, onDone: clear })} />
        )}</div> : (
          <div className="m-4 flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
            <span className="text-sm">You're previewing <strong>#{name}</strong>.</span>
            <Button onClick={() => void join()}>Join channel</Button>
          </div>
        )}
        </div>
        </>}
      </div>

      {threadRootId && <ThreadPanel conversationId={id} rootId={threadRootId} memberIds={memberIds} ack={ackCtx} full={phone} bubbles={bubbles ? bubbleNames(conv.kind) : undefined} subtitle={conv.kind === 'channel' ? `#${name}` : name} canReply={acts.reply} canSchedule={conv.kind === 'channel' || conv.kind === 'dm' || conv.kind === 'group_dm'} onClose={closeThread} />}
      <PinsModal open={modal === 'pins'} onClose={() => setModal(null)} conversationId={id} />
      {modal === 'form' && ws && <ChannelFormModal conversationId={id} workspaceId={ws} current={conv.form_template_id ?? null} onClose={() => setModal(null)}
        onSaved={() => { void detail.refetch(); void formInfo.refetch(); void messageStore.catchUp(id); }} />}
      <NewChannelModal open={modal === 'copy'} onClose={() => setModal(null)} fromChannelId={conv.id} />
      {modal === 'room' && <RoomSettingsModal conv={conv} onClose={() => setModal(null)} />}
      {conv.kind === 'entity' && <MembersModal open={modal === 'members'} onClose={() => setModal(null)} conversation={conv} />}
      {modal === 'settings' && <ChannelSettingsModal conv={conv} initialTab={settingsTab} moderator={moderator} canAssignForm={Boolean(formInfo.data?.can_assign)} onClose={() => setModal(null)}
        onOpenForm={() => setModal('form')} onOpenMembers={() => { setModal(null); setReaders(true); }} onLeave={() => { setModal(null); void leave(); }} />}
      {ackDraft && <AckRequestModal draft={ackDraft} onClose={() => setAckDraft(null)} />}
      {pollDraft && <PollCreateModal conversationId={id} where={conv.kind === 'channel' ? `#${name}` : name} initial={pollDraft} onClose={() => setPollDraft(null)} />}
    </div>
  );
}

/** t123: from a project's channel, open the project, or the project and its channel side by side. */
function MaybeThreadPrefs({ on, conversationId, canReply, children }: { on: boolean; conversationId: string; canReply: boolean; children: React.ReactNode }) {
  return on ? <ThreadPrefsProvider conversationId={conversationId} canReply={canReply}>{children}</ThreadPrefsProvider> : <>{children}</>;
}

function ProjectLinks({ projectId, narrow }: { projectId: string; narrow: boolean }) {
  const projects = useProjects();
  const p = projects.data?.projects.find(x => x.id === projectId);
  return (
    <div className="flex shrink-0 items-center rounded-lg border border-slate-200 text-xs dark:border-slate-700">
      <Link to={`/projects/${projectId}`} data-testid="channel-project-link" title={p ? `Open the project ${p.name}` : 'Open the project'}
        className="flex items-center gap-1 rounded-l-lg px-2 py-1 font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800">
        <FolderKanban className="h-3.5 w-3.5" />{!narrow && <span className="max-w-[10rem] truncate">{p?.name ?? 'Project'}</span>}
      </Link>
      {!narrow && <Link to={`/projects/${projectId}?split=1`} data-testid="channel-split-link" title="Tasks and chat side by side" aria-label="Tasks and chat side by side"
        className="flex items-center border-l border-slate-200 px-1.5 py-1 text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"><Columns2 className="h-3.5 w-3.5" /></Link>}
    </div>
  );
}

